import { FINANCIAL_FORECAST_REDUCER_KEY } from "../index";
import { getUnitSymbolByUnit, itemType, unitType } from "../types";
import { years } from "store/utils/years";
import { defaultZeroMonths } from "./staticMonthsData";
import { GeneralCoType } from "constants/companyTypes";
import * as actionTypes from "../../actions/actionTypes";

export const GENERAL_OTHER_REVENUE_REDUCER_KEY = "generalOtherRevenue";
export const GENERAL_OTHER_REVENUE_KEY = "generalOtherRevenueItem";
export const COST_PRICE_KEY = "costPrice";
export const COST_OF_SALES_KEY = "costOfSales";
export const GENERAL_OTHER_REVENUE_INSTANCES_KEY =
  "generalOtherRevenueInstances";
export const GENERAL_OTHER_REVENUE_TOTAL_INCOME_KEY =
  "generalOtherRevenueTotalIncome";
export const TOTAL_COST_OF_OTHER_REVENUE_KEY = "totalCostOfOtherRevenue";

export const otherRevenueInstanceItems = [
  GENERAL_OTHER_REVENUE_KEY,
  COST_PRICE_KEY,
  COST_OF_SALES_KEY,
];
export const otherRevenueTotalIncomeItems = [
  GENERAL_OTHER_REVENUE_TOTAL_INCOME_KEY,
  TOTAL_COST_OF_OTHER_REVENUE_KEY,
];

const shouldUseMonthly = (getReducerFunc) => {
  if (!getReducerFunc) {
    return false;
  }
  try {
    const forecastState = getReducerFunc(FINANCIAL_FORECAST_REDUCER_KEY);
    return (
      forecastState?.timeSeriesAssumptions?.primaryPeriodicity === "Monthly"
    );
  } catch (err) {
    return false;
  }
};

const getPeriodValue = (item, key, getReducerFunc) => {
  if (!item) {
    return 0;
  }
  if (shouldUseMonthly(getReducerFunc)) {
    const monthKey = item.months?.[key] ?? item.months?.[String(key)];
    if (monthKey !== undefined) {
      return Number(monthKey) || 0;
    }
  }
  return Number(item.years?.[key] || 0);
};

export const otherRevenueInstanceState = {
  [GENERAL_OTHER_REVENUE_KEY]: {
    title: "Other Revenue",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    calculate: () => 0,
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [COST_PRICE_KEY]: {
    title: "Cost Price",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    calculate: () => 0,
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [COST_OF_SALES_KEY]: {
    title: "Cost of Sales",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      key,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (!instanceId) {
        return 0;
      }

      const instance = getReducerFunc(GENERAL_OTHER_REVENUE_REDUCER_KEY)[
        GENERAL_OTHER_REVENUE_INSTANCES_KEY
      ][instanceId];
      return getPeriodValue(instance[COST_PRICE_KEY], key, getReducerFunc);
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

export const initialState = {
  [GENERAL_OTHER_REVENUE_INSTANCES_KEY]: {},
  [GENERAL_OTHER_REVENUE_TOTAL_INCOME_KEY]: {
    title: "Income from Other Revenues",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const otherRevenueState = getReducerFunc(
        GENERAL_OTHER_REVENUE_REDUCER_KEY
      );
      const instances = otherRevenueState[GENERAL_OTHER_REVENUE_INSTANCES_KEY];

      let total = 0;
      Object.values(instances).forEach((instanceData) => {
        total += getPeriodValue(
          instanceData[GENERAL_OTHER_REVENUE_KEY],
          yearKey,
          getReducerFunc
        );
      });

      return total;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [TOTAL_COST_OF_OTHER_REVENUE_KEY]: {
    title: "Cost of Other Revenues",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const otherRevenueState = getReducerFunc(
        GENERAL_OTHER_REVENUE_REDUCER_KEY
      );
      const instances = otherRevenueState[GENERAL_OTHER_REVENUE_INSTANCES_KEY];

      let total = 0;
      Object.values(instances).forEach((instanceData) => {
        total += getPeriodValue(
          instanceData[COST_PRICE_KEY],
          yearKey,
          getReducerFunc
        );
      });

      return total;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

export const defaultOtherRevenueState = {
  ...otherRevenueInstanceState,
};

const generalOtherRevenueReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.ADD_GENERAL_OTHER_REVENUE_INSTANCE:
      return {
        ...state,
        [GENERAL_OTHER_REVENUE_INSTANCES_KEY]: {
          ...state[GENERAL_OTHER_REVENUE_INSTANCES_KEY],
          [action.payload.id]: { ...defaultOtherRevenueState },
        },
      };
    case actionTypes.UPDATE_GENERAL_OTHER_REVENUE_INSTANCE:
      return {
        ...state,
        [GENERAL_OTHER_REVENUE_INSTANCES_KEY]: {
          ...state[GENERAL_OTHER_REVENUE_INSTANCES_KEY],
          [action.payload.id]: {
            ...state[GENERAL_OTHER_REVENUE_INSTANCES_KEY][action.payload.id],
            ...action.payload.data,
          },
        },
      };
    case actionTypes.REMOVE_GENERAL_OTHER_REVENUE_INSTANCE: {
      const { [action.payload.id]: removed, ...remaining } =
        state[GENERAL_OTHER_REVENUE_INSTANCES_KEY];
      return {
        ...state,
        [GENERAL_OTHER_REVENUE_INSTANCES_KEY]: remaining,
      };
    }
    case actionTypes.SET_GENERAL_OTHER_REVENUE_INSTANCE_BY_MONTH: {
      const instance = state[GENERAL_OTHER_REVENUE_INSTANCES_KEY][action.id];
      const item = instance[action.itemKey];
      return {
        ...state,
        [GENERAL_OTHER_REVENUE_INSTANCES_KEY]: {
          ...state[GENERAL_OTHER_REVENUE_INSTANCES_KEY],
          [action.id]: {
            ...instance,
            [action.itemKey]: {
              ...item,
              months: {
                ...item.months,
                [action.monthKey]: action.amount,
              },
            },
          },
        },
      };
    }
    case actionTypes.REPLACE_GENERAL_OTHER_REVENUE_INSTANCE_MONTHS: {
      const instance = state[GENERAL_OTHER_REVENUE_INSTANCES_KEY][action.id];
      return {
        ...state,
        [GENERAL_OTHER_REVENUE_INSTANCES_KEY]: {
          ...state[GENERAL_OTHER_REVENUE_INSTANCES_KEY],
          [action.id]: {
            ...instance,
            [action.itemKey]: {
              ...instance[action.itemKey],
              months: action.payload,
            },
          },
        },
      };
    }
    case actionTypes.RESET_GENERAL_OTHER_REVENUE_REDUCER:
      return initialState;
    default:
      return state;
  }
};

export default generalOtherRevenueReducer;
